import { useCallback, useMemo } from 'react';

import {
  previewAudioStreamAtom,
  useMicrophoneContext,
} from '@repo/videa-notes';
import { isToday } from 'date-fns';
import { useAtomValue } from 'jotai';

import type { EventName } from '@/utils/mixpanel';
import { useTrackEventWithOptionalParams } from '@/utils/mixpanel';
import { getSystemMetrics } from '@/utils/systemMetrics';

import type { VideaNotesPatient } from '../context/VideaNotesCoreContext';
import type { VoiceNotesSource } from '../types';

interface UseVideaNotesAnalyticsParams {
  patient: VideaNotesPatient | undefined;
  estimatedVisitId: number | string | null | undefined;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  selectedTemplateId: string | undefined;
  selectedTemplateName: string | null;
  selectedDate: Date | null;
  voiceNotesSource: VoiceNotesSource;
}

/**
 * Hook for tracking Videa Notes analytics events.
 * Automatically includes microphone state and patient context in all events.
 */
export const useVideaNotesAnalytics = ({
  patient,
  estimatedVisitId,
  unifiedAppointmentId,
  standalonePatientVisitId,
  selectedTemplateId,
  selectedTemplateName,
  selectedDate,
  voiceNotesSource,
}: UseVideaNotesAnalyticsParams) => {
  const { trackEvent } = useTrackEventWithOptionalParams();

  // Read microphone state from context - these are only needed for analytics
  const {
    hasPermission: hasMicrophonePermission,
    devices: availableDevices,
    selectedDeviceId,
  } = useMicrophoneContext();
  const previewStream = useAtomValue(previewAudioStreamAtom);
  const isStreamDetected = previewStream?.active === true;

  // Build analytics payload internally
  const eventTrackingPayload = useMemo(
    () => ({
      patient: {
        imagingPatientId: patient?.imagingPatientId,
        pmsPatientId: patient?.pmsPatientId,
        videaId: patient?.videaId,
      },
      hasMicrophonePermission,
      availableDevices,
      selectedDeviceId: selectedDeviceId || '',
      isStreamDetected,
      estimatedVisitId,
      unifiedAppointmentId,
      standalonePatientVisitId,
      templateId: selectedTemplateId,
      templateName: selectedTemplateName,
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
      voiceNotesSource,
    }),
    [
      patient,
      hasMicrophonePermission,
      availableDevices,
      selectedDeviceId,
      isStreamDetected,
      estimatedVisitId,
      unifiedAppointmentId,
      standalonePatientVisitId,
      selectedTemplateId,
      selectedTemplateName,
      selectedDate,
      voiceNotesSource,
    ],
  );

  const trackVideaNotesEvent = useCallback(
    (event: EventName, additionalPayload?: Record<string, unknown>) => {
      // Capture memory metrics at the time of the event
      const systemMetrics = getSystemMetrics();
      
      trackEvent(event, { 
        ...eventTrackingPayload, 
        ...additionalPayload,
        ...systemMetrics, // Spread flat so Mixpanel can track each metric
      });
    },
    [trackEvent, eventTrackingPayload],
  );

  return { trackVideaNotesEvent };
};
